import { LineType, QuotationQuoteLine, QuoteAssignment } from '../models/quote.model';
import { ConfigitQuote, Quote, QuoteLine } from '../services/quote.service';
import { PimProductInfo } from '../services/products.service';
import * as uuid from 'uuid';
import { BomItem } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';

export interface HasAssignments {
    assignments: QuoteAssignment[];
}

export abstract class QuoteLineAdapter<L extends HasAssignments> {
    public abstract getLineByType(quote: Quote, type: LineType): QuotationQuoteLine;

    public abstract getLineByMaterial(quote: Quote, material: string): L;

    public abstract getExternalProducts(quote: Quote): { material: string; quantity: number }[];

    public abstract pruneProductItems(materials: string[], quote: Quote): boolean;

    public abstract ensureProductItem(
        bomItemId: string,
        quantity: number,
        materialInfo: PimProductInfo,
        quote: Quote
    ): boolean;

    protected abstract getLineByTypeInternal(quote: Quote, type: LineType): L;

    protected abstract addConfigurationLine(
        type: LineType,
        material: string,
        assignments: QuoteAssignment[],
        quote: Quote
    ): void;

    public createConfigurationLine(
        material: string,
        type: LineType,
        bomItems: (BomItem & { info: PimProductInfo })[],
        assignments: QuoteAssignment[],
        quote: Quote
    ): L {
        if (!quote) {
            return undefined;
        }

        let line = this.getLineByTypeInternal(quote, type);
        if (line) {
            // merge assignments into line.assignments
            const replaced = line.assignments.map((a1) => ({
                ...a1,
                ...assignments.find((a2) => a2.variableName === a1.variableName),
            }));
            const newAssignments = assignments.filter(
                (a1) => !line.assignments.some((a2) => a2.variableName === a1.variableName)
            );
            this.setLineAssignments([...replaced, ...newAssignments], line);
        } else {
            this.addConfigurationLine(type, material, assignments, quote);
        }
        bomItems.forEach((bomItem) =>
            this.ensureProductItem(bomItem.itemId, bomItem.componentQuantity, bomItem.info, quote)
        );
        return line;
    }

    public setLineAssignments(assignments: QuoteAssignment[], line: HasAssignments) {
        line.assignments = assignments
            .filter((assignment) => !assignment.variableName.startsWith('SapData'))
            .map(({ variableName, valueName, isUserAssignment, isDefault }) => ({
                variableName,
                valueName,
                isUserAssignment,
                isDefault,
            }));
    }
}

export class ConfigitQuoteLineAdapter extends QuoteLineAdapter<QuoteLine> {
    getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        const line = this.getLineByTypeInternal(quote, type);
        return line
            ? {
                  type,
                  material: line.variantCode,
                  assignments: line.assignments,
              }
            : undefined;
    }

    getLineByMaterial(quote: Quote, material: string): QuoteLine {
        return quote.configitQuote.lines.find((l) => l.variantCode === material);
    }

    getExternalProducts(quote: Quote) {
        const lines = this.getLines(quote.configitQuote, LineType.product);
        return lines.map((l) => ({ material: l.variantCode, quantity: l.quantity }));
    }

    pruneProductItems(materials: string[], quote: Quote): boolean {
        if (quote) {
            const removed = this.getSubLines(quote.configitQuote).filter(
                (line) => !materials.includes(line.variantCode)
            );
            if (removed.length) {
                removed.forEach((line) => {
                    const index = quote.configitQuote.lines.findIndex((l) => l.lineId === line.lineId);
                    quote.configitQuote.lines.splice(index, 1);
                });
                return true;
            }
        }
        return false;
    }

    getLineByTypeInternal(quote: Quote, type: LineType): QuoteLine {
        const lines = this.getLines(quote.configitQuote, type);
        return lines?.length === 1 ? lines[0] : undefined;
    }

    ensureProductItem(bomItemId: string, quantity: number, productInfo: PimProductInfo, quote: Quote): boolean {
        if (quote) {
            const items = this.getSubLines(quote.configitQuote, productInfo.name);
            let changed: boolean;
            if (items.length) {
                // line for material exists, but quantity may have changed
                const existing = items[0];
                changed = existing.quantity !== quantity;
                existing.quantity = quantity;
            } else {
                // no line for material exists, create one
                const subline = this.createSubLine(quote, bomItemId, quantity, productInfo);
                if (subline) {
                    quote.configitQuote.lines.push(subline);
                    changed = true;
                }
            }

            return changed;
        }
        return false;
    }

    addConfigurationLine(type: LineType, material: string, assignments: QuoteAssignment[], quote: Quote) {
        const line = this.createConfigitLine(quote.configitQuote, 1, { name: material } as PimProductInfo, type);
        this.setLineAssignments(assignments, line);
        // if there were any lines of that type before, remove them
        quote.configitQuote.lines = quote.configitQuote.lines.filter((l) => this.getLineType(l) !== type);
        quote.configitQuote.lines.push(line);
    }

    private getLines(quote: ConfigitQuote, type: LineType) {
        return quote.lines.filter((l) => this.getLineType(l) === type);
    }

    private getLineType(line: QuoteLine): LineType {
        return <LineType>line.properties.find((p) => p.key === 'LineType')?.value?.replace(/"/g, '');
    }

    private getSubLines(quote: ConfigitQuote, ...materials: string[]): QuoteLine[] {
        const lines = quote.lines.filter((l) => l.isSubBomItem);
        return materials?.length ? lines.filter((l) => materials.includes(l.variantCode)) : lines;
    }

    private createSubLine(quote: Quote, bomItemId: string, quantity: number, materialInfo: PimProductInfo): QuoteLine {
        let line: QuoteLine = undefined;
        const parentId =
            this.getLineByTypeInternal(quote, LineType.tertiary)?.lineId ||
            this.getLineByTypeInternal(quote, LineType.secondary)?.lineId ||
            this.getLineByTypeInternal(quote, LineType.primary)?.lineId;
        if (parentId) {
            line = this.createConfigitLine(quote.configitQuote, quantity, materialInfo);
            line.bomItemId = bomItemId;
            line.isSubBomItem = true;
            line.parentId = parentId;
        }
        return line;
    }

    private createConfigitLine(
        quote: ConfigitQuote,
        quantity: number,
        materialInfo: PimProductInfo,
        type?: LineType
    ): QuoteLine {
        const id = uuid.v4();

        return {
            language: quote.language,
            id,
            quantity,
            properties: type
                ? [
                      {
                          key: 'LineType',
                          value: `"${type}"`,
                      },
                  ]
                : [],
            materialVariant: {
                language: quote.language,
                code: materialInfo.name,
                image: '',
                assignments: [],
                material: {
                    language: quote.language,
                    properties: [],
                    name: materialInfo.name,
                    externalId: materialInfo.name,
                    description: {
                        serializedTranslations: [],
                    },
                },
            },
            variantCode: materialInfo.name,
            description: {
                serializedTranslations: [
                    {
                        language: materialInfo?.locale || '',
                        translation: materialInfo?.description || '',
                        isDefault: true,
                    },
                ],
            },
            lineType: 0,
            createdDate: new Date().toISOString(),
            presetSet: false,
            modelLineType: 0,
            lineId: id,
            assignments: [],
            valid: true,
            bomItemId: '',
        };
    }
}

export class QuotationQuoteLineAdapter extends QuoteLineAdapter<QuotationQuoteLine> {
    getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        return this.getLineByTypeInternal(quote, type);
    }

    getLineByMaterial(quote: Quote, material: string): QuotationQuoteLine {
        return quote.quotationQuote.offer.extraData.lines.find((l) => l.material === material);
    }

    getExternalProducts(quote: Quote): { material: string; quantity: number }[] {
        return quote.quotationQuote.offer.items.map((i) => ({ material: i.material, quantity: i.quantity }));
    }

    pruneProductItems(): boolean {
        // do nothing
        return false;
    }

    ensureProductItem(): boolean {
        // do nothing
        return false;
    }

    addConfigurationLine(type: LineType, material: string, assignments: QuoteAssignment[], quote: Quote) {
        const line: QuotationQuoteLine = { type, assignments: [], material };
        this.setLineAssignments(assignments, line);
        const extraData = quote.quotationQuote.offer.extraData;
        // if there were any lines of that type before, remove them
        extraData.lines = extraData.lines.filter((l) => l.type !== type);
        extraData.lines.push(line);
    }

    getLineByTypeInternal(quote: Quote, type: LineType): QuotationQuoteLine {
        return quote.quotationQuote.offer.extraData.lines.find((l) => l.type === type);
    }
}
